// cards.js — JarJar renderings of TamCentralStation's Performance cards
// (HeatmapGridCard, TimingTableCard, DockTimingTableCard, TopImpactGrid,
// DriverBreakdownView, MoveTable/AndonTable). Same columns, cell sizes, heat
// bins, footers and click-through modals; plain React instead of Meridian.
import React, { useMemo, useState } from 'react';
import { heatCell, heatRatioCell, CHROME } from './heatScale';
import { computeFooter } from './tcs/heatmapStyle';
import { fmtDuration, avgSecs, negativeHeatRatio } from './tcs/TimingTable';
import { resolveDockCell } from './tcs/timingCellEvents';
import { TDR_DEPTS, doorDept } from './doorDepts';
import { fmtDur, fmtTime, fmt, pairsToImpactRows, buildDriverRows } from './tcs/utils';

const INK = { secondary: '#697576', success: '#348118', danger: '#DE1B1B' };
const txt = (secondary) => ({ fontSize: 12, color: secondary ? INK.secondary : '#0B0C0C' });
const shell = { background: '#fff', border: CHROME.border, borderRadius: 8, padding: 16, display: 'inline-block', verticalAlign: 'top', margin: '0 16px 16px 0', maxWidth: '100%' };
const shellTitle = { fontSize: 14, fontWeight: 700, margin: '0 0 10px' };
const cellBase = { height: 22, borderRadius: 3, fontSize: 12, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden', border: CHROME.border };

export function Card({ title, accessory, children, style }) {
  return (
    <div style={{ ...shell, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h4 style={shellTitle}>{title}</h4>{accessory}
      </div>
      {children}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(11,12,12,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 8, width: 900, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const th = { textAlign: 'left', fontSize: 12, fontWeight: 600, color: INK.secondary, padding: '6px 8px', borderBottom: CHROME.border, position: 'sticky', top: 0, background: '#fff' };
const td = { fontSize: 13, padding: '6px 8px', borderBottom: CHROME.border, whiteSpace: 'nowrap' };
function Tag({ concept, children }) {
  const bg = { success: '#D6ECC9', danger: '#FFD9CF', warning: '#FFE8B1', neutral: '#E7E9E9' }[concept] || '#E7E9E9';
  return <span style={{ background: bg, borderRadius: 4, padding: '1px 6px', fontSize: 12 }}>{children}</span>;
}
function Table({ headers, children }) {
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead><tr>{headers.map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  );
}
const noAt = u => (u || '').replace('@amazon.com', '');

const MOVE_STATUS = { completed: ['success', 'Completed'], cancelled: ['danger', 'Cancelled'], inProgress: ['warning', 'In progress'], pending: ['neutral', 'Pending'] };
function MoveTable({ rows, formatDuration = fmtDur }) {
  const sorted = [...rows].sort((a, b) => (b.bucketTs ?? 0) - (a.bucketTs ?? 0));
  return (
    <Table headers={['Created By', 'Driver', 'From', 'To', 'Duration', 'Vehicle #', 'Start', 'End', 'Status']}>
      {sorted.map((r, i) => {
        const [concept, label] = MOVE_STATUS[r.status] || MOVE_STATUS.pending;
        return (
          <tr key={`${r.eventId ?? 'row'}-${r.bucketTs ?? i}`}>
            <td style={td}>{noAt(r.createdBy) || '—'}</td><td style={td}>{noAt(r.userId) || '—'}</td><td style={td}>{r.fromLocation || '—'}</td><td style={td}>{r.toLocation || '—'}</td>
            <td style={td}>{r.duration != null ? formatDuration(r.duration) : '—'}</td><td style={td}>{r.vehicleNumber || '—'}</td>
            <td style={td}>{r.startTs != null ? fmt(r.startTs) : '—'}</td><td style={td}>{r.completeTs != null ? fmt(r.completeTs) : '—'}</td>
            <td style={td}><Tag concept={concept}>{label}</Tag>{r.status === 'cancelled' && r.cancelTime != null && <div style={txt(true)}>cancelled @ {fmt(r.cancelTime)}</div>}</td>
          </tr>
        );
      })}
    </Table>
  );
}
const ANDON_STATUS = { completed: ['success', 'Completed'], cancelled: ['danger', 'Cancelled'], open: ['neutral', 'Open'] };
function AndonTable({ rows, formatDuration = fmtDur }) {
  const sorted = [...rows].sort((a, b) => (b.bucketTs ?? 0) - (a.bucketTs ?? 0));
  return (
    <Table headers={['Andon', 'Location', 'Vehicle', 'Status', 'Time', 'Start', 'End', 'Duration']}>
      {sorted.map((r, i) => {
        const done = r.status === 'completed';
        const [concept, label] = ANDON_STATUS[r.status] || ANDON_STATUS.open;
        return (
          <tr key={`${r.eventId ?? 'row'}-${r.bucketTs ?? i}`}>
            <td style={td}>{(r.suffix || '').replace(/_/g, ' ') || '—'}</td><td style={td}>{r.location || '—'}</td><td style={td}>{r.vehicleNumber || '—'}</td>
            <td style={td}><Tag concept={concept}>{label}</Tag>{r.status === 'cancelled' && r.cancelTime != null && <div style={txt(true)}>cancelled @ {fmt(r.cancelTime)}</div>}</td>
            <td style={td}>{r.bucketTs != null ? fmt(r.bucketTs) : '—'}</td>
            <td style={td}>{done && r.startTs != null ? fmt(r.startTs) : '—'}</td><td style={td}>{done && r.completeTs != null ? fmt(r.completeTs) : '—'}</td>
            <td style={td}>{done && r.duration != null ? formatDuration(r.duration) : '—'}</td>
          </tr>
        );
      })}
    </Table>
  );
}

// ── HeatmapGridCard ───────────────────────────────────────────────────────
function HeatCellBtn({ value, heat, max, dataBar, isTotal, ariaLabel, onClick }) {
  const style = { ...cellBase, width: 48, fontWeight: value > 0 ? 600 : 400 };
  if (!heat) return <button type="button" className="jj-heat-cell" style={{ ...style, ...CHROME.gray }} aria-label={ariaLabel} onClick={onClick}>{value > 0 ? value : ''}</button>;
  if (isTotal && dataBar) {
    const pct = Math.round((value / max) * 100);
    return (
      <button type="button" className="jj-heat-cell" style={{ ...style, background: '#fff' }} aria-label={ariaLabel} onClick={onClick}>
        <span aria-hidden="true" style={{ position: 'absolute', inset: 0, right: `${100 - pct}%`, background: heat.background, pointerEvents: 'none' }} />
        <span style={{ position: 'relative', zIndex: 1 }}>{value}</span>
      </button>
    );
  }
  return <button type="button" className="jj-heat-cell" style={{ ...style, ...heat }} aria-label={ariaLabel} onClick={onClick}>{value}</button>;
}

export function HeatmapGridCard({ title, rows = [], colorScale = 'heat', dataBar = true, summaryType = 'total', series = '', resolveCellEvents }) {
  const [openCell, setOpenCell] = useState(null);
  const { quarterMax, totalMax } = useMemo(() => rows.length
    ? { quarterMax: Math.max(1, ...rows.flatMap(r => [r.q1, r.q2, r.q3, r.q4])), totalMax: Math.max(1, ...rows.map(r => r.total)) }
    : { quarterMax: 1, totalMax: 1 }, [rows]);
  const summaryLine = useMemo(() => {
    if (!rows.length) return null;
    const sumTotal = rows.reduce((s, r) => s + r.total, 0);
    const maxTotal = Math.max(0, ...rows.map(r => r.total));
    if (!sumTotal || !maxTotal) return null;
    let peak = rows[0]; for (const r of rows) if (r.total > peak.total) peak = r;
    if (summaryType === 'average') {
      const active = rows.filter(r => r.q1 || r.q2 || r.q3 || r.q4).length;
      return `avg ${Math.round(sumTotal / (active || 1))} drivers/hr · peak ${maxTotal}`;
    }
    return `${sumTotal} total · peak ${maxTotal} at ${peak.hour}`;
  }, [rows, summaryType]);
  const footer = useMemo(() => computeFooter(rows, summaryType, null), [rows, summaryType]);
  const canOpen = typeof resolveCellEvents === 'function';
  const click = (hour, hourIndex, quarter, value) => canOpen ? () => setOpenCell({ hour, hourIndex, quarter, series, value }) : undefined;
  const modalRows = openCell && canOpen ? (resolveCellEvents(openCell) || []) : [];
  const qLabel = openCell ? (openCell.quarter === null ? 'Hour total' : `Q${openCell.quarter + 1}`) : '';
  return (
    <Card title={title}>
      {summaryLine && <div style={{ ...txt(true), marginBottom: 6 }}>{summaryLine}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(5, 48px)', gap: 2, alignItems: 'center' }}>
        {['Hour', 'Q1', 'Q2', 'Q3', 'Q4', 'Total'].map((h, i) => <div key={h} style={{ ...txt(true), textAlign: i === 0 ? 'right' : 'center', paddingRight: i === 0 ? 4 : 0 }}>{h}</div>)}
        {rows.length === 0
          ? <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '8px 0', ...txt(true) }}>No data loaded.</div>
          : rows.map((row, hi) => (
            <React.Fragment key={`row-${series}-${hi}`}>
              <div style={{ ...txt(false), textAlign: 'right', paddingRight: 4 }}>{row.hour}</div>
              {['q1', 'q2', 'q3', 'q4'].map((qk, q) => (
                <HeatCellBtn key={qk} value={row[qk]} heat={heatCell(row[qk], quarterMax, colorScale)} max={quarterMax} dataBar={false} isTotal={false}
                  ariaLabel={`${title}, ${row.hour}, Q${q + 1}: ${row[qk]}`} onClick={click(row.hour, hi, q, row[qk])} />
              ))}
              <HeatCellBtn value={row.total} heat={heatCell(row.total, totalMax, colorScale)} max={totalMax} dataBar={dataBar} isTotal
                ariaLabel={`${title}, ${row.hour}, Total: ${row.total}`} onClick={click(row.hour, hi, null, row.total)} />
            </React.Fragment>
          ))}
        {rows.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', paddingRight: 4 }}>{footer.label}</div>
            {[footer.sq1, footer.sq2, footer.sq3, footer.sq4, footer.st].map((v, i) => <div key={i} style={{ ...txt(false), textAlign: 'center' }}>{v || ''}</div>)}
          </>
        )}
      </div>
      {openCell && canOpen && (
        <Modal title={`${title} — ${openCell.hour} ${qLabel}`} onClose={() => setOpenCell(null)}>
          {modalRows.length === 0 ? <div style={txt(true)}>No events in this interval.</div>
            : modalRows[0].kind === 'move' ? <MoveTable rows={modalRows} />
            : modalRows[0].kind === 'andon' ? <AndonTable rows={modalRows} />
            : (
              <Table headers={['Time', 'Driver', 'Detail']}>
                {[...modalRows].sort((a, b) => (b.firstTimestamp ?? 0) - (a.firstTimestamp ?? 0)).map((r, i) => {
                  const ts = r.timestamp ?? r.firstTimestamp ?? null;
                  return <tr key={`${r.userId ?? r.eventId ?? 'row'}-${ts ?? i}`}><td style={td}>{ts != null ? fmtTime(ts) : '—'}</td><td style={td}>{noAt(r.userId) || '—'}</td><td style={td}>{r.count != null ? `${r.count} moves` : (r.eventType || r.vehicleNumber || '')}</td></tr>;
                })}
              </Table>
            )}
        </Modal>
      )}
    </Card>
  );
}

// ── Timing cards ─────────────────────────────────────────────────────────
function TimingCellBtn({ value, ratio, children, ariaLabel, onClick, plain }) {
  const has = value != null;
  let fill = CHROME.gray;
  if (has) fill = plain || ratio === undefined ? { background: '#fff' } : (heatRatioCell(ratio) || CHROME.gray);
  return <button type="button" className="jj-heat-cell" style={{ ...cellBase, width: 72, fontWeight: has ? 600 : 400, ...fill }} aria-label={ariaLabel} onClick={onClick}>{children}</button>;
}
function TimingHeader({ label, noWrap }) {
  const parts = label.split('→');
  return <div style={{ ...txt(true), textAlign: 'center', whiteSpace: noWrap ? 'nowrap' : 'normal' }}>{parts.length > 1 ? <>{parts[0]}<br />→{parts.slice(1).join('→')}</> : label}</div>;
}
function TimingGridCard({ title, rows, fields, labels, ratioFields, extraField, footerVals, resolveCellEvents }) {
  const [openCell, setOpenCell] = useState(null);
  const canOpen = typeof resolveCellEvents === 'function';
  const click = (hourIndex, metricField, hourLabel) => canOpen ? () => setOpenCell({ hourIndex, metricField, hourLabel }) : undefined;
  const modalRows = openCell && canOpen ? (resolveCellEvents(openCell) || []) : [];
  const cols = fields.length + (extraField ? 1 : 0);
  return (
    <Card title={title}>
      <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${cols}, 72px)`, gap: 2, alignItems: 'center' }}>
        <div style={{ ...txt(true), textAlign: 'right', paddingRight: 4 }}>Hour</div>
        {fields.map(f => <TimingHeader key={f} label={labels[f]} />)}
        {extraField && <TimingHeader label={labels[extraField]} noWrap />}
        {rows.length === 0
          ? <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '8px 0', ...txt(true) }}>No data loaded.</div>
          : rows.map((row, hi) => (
            <React.Fragment key={row.hour}>
              <div style={{ ...txt(false), textAlign: 'right', paddingRight: 4 }}>{row.hour}</div>
              {fields.map(f => (
                <TimingCellBtn key={f} value={row[f]} ratio={row[ratioFields[f]]} ariaLabel={`${title}, ${row.hour}, ${labels[f]}`} onClick={click(hi, f, row.hour)}>
                  {fmtDuration(row[f])}
                </TimingCellBtn>
              ))}
              {extraField && (() => {
                const n = row[extraField] || 0;
                return <TimingCellBtn plain value={n > 0 ? n : null} ariaLabel={`${title}, ${row.hour}, ${labels[extraField]}`} onClick={click(hi, extraField, row.hour)}>{n > 0 ? n : ''}</TimingCellBtn>;
              })()}
            </React.Fragment>
          ))}
        {rows.length > 0 && footerVals && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', paddingRight: 4 }}>Avg</div>
            {footerVals.map((v, i) => <div key={i} style={{ ...cellBase, width: 72, cursor: 'default', fontWeight: 600 }}>{v}</div>)}
          </>
        )}
      </div>
      {openCell && canOpen && (
        <Modal title={`${title} — ${openCell.hourLabel} · ${labels[openCell.metricField]}`} onClose={() => setOpenCell(null)}>
          {modalRows.length === 0 ? <div style={txt(true)}>No events in this interval.</div>
            : openCell.metricField === 'andonResponse' ? <AndonTable rows={modalRows} formatDuration={fmtDuration} />
            : <MoveTable rows={modalRows} formatDuration={fmtDuration} />}
        </Modal>
      )}
    </Card>
  );
}

const T_FIELDS = ['createToStart', 'startToComplete', 'createToComplete', 'andonResponse'];
const T_LABELS = { createToStart: 'Create→Start', startToComplete: 'Start→Complete', createToComplete: 'Create→Complete', andonResponse: 'Andon Response' };
const T_RATIO = { createToStart: '_createToStartRatio', startToComplete: '_startToCompleteRatio', createToComplete: '_createToCompleteRatio', andonResponse: '_andonResponseRatio' };
export function TimingTableCard({ title, rows = [], resolveCellEvents }) {
  const footer = rows.length ? T_FIELDS.map(f => { const raw = rows._allDurations?.[f]; return raw?.length ? fmtDuration(avgSecs(raw)) : ''; }) : null;
  return <TimingGridCard title={title} rows={rows} fields={T_FIELDS} labels={T_LABELS} ratioFields={T_RATIO} footerVals={footer} resolveCellEvents={resolveCellEvents} />;
}

const D_FIELDS = ['completeToTdrDock', 'loadCompleteToCreate', 'tdrReleaseToCreate'];
const D_LABELS = { completeToTdrDock: 'Complete→TDR Dock', loadCompleteToCreate: 'Detach→Create', tdrReleaseToCreate: 'TDR Rel→Create', loadCompleteToCreateOver30: 'Detach→Create >30m' };
const D_RATIO = { completeToTdrDock: '_completeToTdrDockRatio', loadCompleteToCreate: '_loadCompleteToCreateRatio', tdrReleaseToCreate: '_tdrReleaseToCreateRatio' };
export function DockTimingTableCard({ title, rows = [], resolveCellEvents }) {
  const footer = rows.length ? [
    ...D_FIELDS.map(f => { const v = rows.map(r => r[f]).filter(x => x != null); return v.length ? fmtDuration(avgSecs(v)) : ''; }),
    String(rows.reduce((s, r) => s + (r.loadCompleteToCreateOver30 || 0), 0) || ''),
  ] : null;
  return <TimingGridCard title={title} rows={rows} fields={D_FIELDS} labels={D_LABELS} ratioFields={D_RATIO} extraField="loadCompleteToCreateOver30" footerVals={footer} resolveCellEvents={resolveCellEvents} />;
}

// ── TDR Rel→Create by Dept (JarJar addition) ────────────────────────────
// Splits TCS's own Dock Timing "TDR Rel→Create" contributors by the department
// of the door the trailer was released from (tdrRelease.location). Same gaps,
// same heat scaling (per column), same click-through rows.
export function TdrDeptCard({ title, dockRows = [], site = 'OLM1' }) {
  const [openCell, setOpenCell] = useState(null);
  const { rows, byDept } = useMemo(() => {
    const byDept = {};
    for (const d of TDR_DEPTS) {
      byDept[d] = dockRows.map(r => {
        const contribs = (r._contributors?.tdrReleaseToCreate || []).filter(c => doorDept(c.tdrRelease?.location, site) === d);
        return { ...r, _contributors: { ...r._contributors, tdrReleaseToCreate: contribs }, tdrReleaseToCreate: avgSecs(contribs.map(c => c.duration)) };
      });
    }
    const rows = dockRows.map((r, i) => {
      const out = { hour: r.hour };
      for (const d of TDR_DEPTS) out[d] = byDept[d][i].tdrReleaseToCreate;
      return out;
    });
    for (const d of TDR_DEPTS) {
      const vals = rows.map(r => r[d]).filter(v => v != null);
      const max = vals.length ? Math.max(...vals) : 0;
      rows.forEach(r => { r[`_${d}Ratio`] = negativeHeatRatio(r[d], max); });
    }
    return { rows, byDept };
  }, [dockRows, site]);
  const visible = TDR_DEPTS.filter(d => d !== 'Other' || rows.some(r => r.Other != null));
  const footer = visible.map(d => { const v = rows.map(r => r[d]).filter(x => x != null); return v.length ? fmtDuration(avgSecs(v)) : ''; });
  const modalRows = openCell ? (resolveDockCell(byDept[openCell.dept], openCell.hourIndex, 'tdrReleaseToCreate') || []) : [];
  return (
    <Card title={title}>
      <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${visible.length}, 72px)`, gap: 2, alignItems: 'center' }}>
        <div style={{ ...txt(true), textAlign: 'right', paddingRight: 4 }}>Hour</div>
        {visible.map(d => <div key={d} style={{ ...txt(true), textAlign: 'center' }}>{d}</div>)}
        {rows.length === 0
          ? <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '8px 0', ...txt(true) }}>No data loaded.</div>
          : rows.map((row, hi) => (
            <React.Fragment key={row.hour}>
              <div style={{ ...txt(false), textAlign: 'right', paddingRight: 4 }}>{row.hour}</div>
              {visible.map(d => (
                <TimingCellBtn key={d} value={row[d]} ratio={row[`_${d}Ratio`]} ariaLabel={`${title}, ${row.hour}, ${d}`}
                  onClick={() => setOpenCell({ hourIndex: hi, dept: d, hourLabel: row.hour })}>
                  {fmtDuration(row[d])}
                </TimingCellBtn>
              ))}
            </React.Fragment>
          ))}
        {rows.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', paddingRight: 4 }}>Avg</div>
            {footer.map((v, i) => <div key={i} style={{ ...cellBase, width: 72, cursor: 'default', fontWeight: 600 }}>{v}</div>)}
          </>
        )}
      </div>
      {openCell && (
        <Modal title={`${title} — ${openCell.hourLabel} · ${openCell.dept}`} onClose={() => setOpenCell(null)}>
          {modalRows.length === 0 ? <div style={txt(true)}>No events in this interval.</div> : <MoveTable rows={modalRows} formatDuration={fmtDuration} />}
        </Modal>
      )}
    </Card>
  );
}

// ── Top Impact ───────────────────────────────────────────────────────────
const IMPACT_COLS = {
  c2c: ['Driver', 'Trailer', 'Path', 'Create', 'Start', 'Complete', 'Duration'],
  c2s: ['Driver', 'Trailer', 'Path', 'Create', 'Start', 'Duration'],
  s2c: ['Driver', 'Trailer', 'Path', 'Start', 'Complete', 'Duration'],
};
function ImpactTable({ title, pairs, type }) {
  const rows = pairsToImpactRows(pairs, type);
  const cols = IMPACT_COLS[type];
  return (
    <Card title={title} style={{ display: 'block', margin: 0 }}>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        <Table headers={cols}>
          {rows.length === 0 ? <tr><td style={{ ...td, color: INK.secondary }} colSpan={cols.length}>No data loaded.</td></tr>
            : rows.map((r, i) => (
              <tr key={`${r.driver}-${r.start ?? ''}-${i}`}>
                <td style={td}>{r.driver}</td><td style={td}>{r.trailer}</td><td style={td}>{r.path}</td>
                {type !== 's2c' && <td style={td}>{fmtTime(r.create)}</td>}
                <td style={td}>{fmtTime(r.start)}</td>
                {type !== 'c2s' && <td style={td}>{fmtTime(r.complete)}</td>}
                <td style={td}>{fmtDur(r.duration)}</td>
              </tr>
            ))}
        </Table>
      </div>
    </Card>
  );
}
export function TopImpactGrid({ onsitePairs = [], offsitePairs = [] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(560px, 1fr))', gap: 16 }}>
      <ImpactTable title="Hostler Create→Complete" pairs={onsitePairs} type="c2c" />
      <ImpactTable title="Hostler Create→Start" pairs={onsitePairs} type="c2s" />
      <ImpactTable title="Hostler Start→Complete" pairs={onsitePairs} type="s2c" />
      <ImpactTable title="Shuttle Create→Complete" pairs={offsitePairs} type="c2c" />
      <ImpactTable title="Shuttle Create→Start" pairs={offsitePairs} type="c2s" />
      <ImpactTable title="Shuttle Start→Complete" pairs={offsitePairs} type="s2c" />
    </div>
  );
}

// ── Transaction Breakdown ────────────────────────────────────────────────
const DB_HEADERS = ['Operator', 'Transactions', 'Hostler Completes', 'Avg H. Time', 'Shuttle Completes', 'Avg S. Time', '<4 Min', 'Andon Completes', 'Avg Andon Time', 'Check Ins', 'Check Outs'];
function DataBarTd({ value, max, scale }) {
  if (!value || !max) return <td style={td} />;
  const cell = heatCell(value, max, scale);
  if (!cell) return <td style={{ ...td, textAlign: 'center' }}>{value}</td>;
  const pct = Math.round((value / max) * 100);
  return (
    <td style={{ ...td, position: 'relative', textAlign: 'center' }}>
      <span style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: `${100 - pct}%`, background: cell.background, pointerEvents: 'none' }} />
      <span style={{ position: 'relative', zIndex: 1, fontSize: 12, fontWeight: 600 }}>{value}</span>
    </td>
  );
}
function GateCheckTd({ effective, raw, label }) {
  if (!effective) return <td style={td} />;
  const collapsed = raw - effective;
  const eff = `${effective} effective ${label}${effective === 1 ? '' : 's'}`;
  const rawL = `${raw} raw ${label} event${raw === 1 ? '' : 's'}`;
  return <td style={{ ...td, textAlign: 'center' }} title={collapsed > 0 ? `${rawL} − ${collapsed} combo vehicle${collapsed === 1 ? '' : 's'} = ${eff}` : `${eff} (${rawL})`}>{effective}</td>;
}
export function DriverBreakdownView({ perfData }) {
  const [showAll, setShowAll] = useState(false);
  const rows = perfData?.phase === 'complete' ? buildDriverRows(perfData, showAll) : [];
  const maxTx = rows.length ? Math.max(...rows.map(r => r.transactions)) : 1;
  const maxHC = rows.length ? Math.max(...rows.map(r => r.hostlerCompletes)) : 1;
  const maxSub = rows.length ? Math.max(...rows.map(r => r.sub4)) : 1;
  const sum = k => rows.reduce((s, r) => s + r[k], 0);
  return (
    <Card title="Transaction Breakdown" style={{ display: 'block', minWidth: 600 }}
      accessory={<label style={{ fontSize: 13 }}><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Show all</label>}>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Table headers={DB_HEADERS}>
          {rows.length === 0 && <tr><td style={{ ...td, color: INK.secondary }} colSpan={DB_HEADERS.length}>No data loaded.</td></tr>}
          {rows.map(r => (
            <tr key={r.uid}>
              <td style={{ ...td, fontWeight: 600 }}>{r.uid}</td>
              <DataBarTd value={r.transactions} max={maxTx} scale="blue" />
              <DataBarTd value={r.hostlerCompletes} max={maxHC} scale="green" />
              <td style={td}>{r.avgHostler != null ? fmtDur(r.avgHostler) : ''}</td>
              <td style={td}>{r.shuttleCompletes || ''}</td>
              <td style={td}>{r.avgShuttle != null ? fmtDur(r.avgShuttle) : ''}</td>
              <DataBarTd value={r.sub4} max={maxSub} scale="red" />
              <td style={td}>{r.andonCompletes || ''}</td>
              <td style={td}>{r.avgAndon != null ? fmtDur(r.avgAndon) : ''}</td>
              <GateCheckTd effective={r.checkIns} raw={r.rawCheckIns} label="check-in" />
              <GateCheckTd effective={r.checkOuts} raw={r.rawCheckOuts} label="check-out" />
            </tr>
          ))}
          {rows.length > 0 && showAll && (
            <tr style={{ fontWeight: 700 }}>
              <td style={td}>Total</td><td style={{ ...td, textAlign: 'center' }}>{sum('transactions')}</td><td style={{ ...td, textAlign: 'center' }}>{sum('hostlerCompletes')}</td><td style={td} />
              <td style={{ ...td, textAlign: 'center' }}>{sum('shuttleCompletes')}</td><td style={td} /><td style={{ ...td, textAlign: 'center' }}>{sum('sub4')}</td>
              <td style={{ ...td, textAlign: 'center' }}>{sum('andonCompletes')}</td><td style={td} />
              <GateCheckTd effective={sum('checkIns')} raw={sum('rawCheckIns')} label="check-in" />
              <GateCheckTd effective={sum('checkOuts')} raw={sum('rawCheckOuts')} label="check-out" />
            </tr>
          )}
        </Table>
      </div>
    </Card>
  );
}

// ── Move History ─────────────────────────────────────────────────────────
export function MoveHistoryView({ pairs = [] }) {
  const [q, setQ] = useState('');
  const shown = pairs.filter(p => !q || [p.start?.userId, p.start?.location, p.complete?.location, p.start?.vehicleNumber]
    .some(v => (v || '').toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => b.complete.timestamp - a.complete.timestamp);
  return (
    <Card title={`Moves (${shown.length})`} style={{ display: 'block' }}
      accessory={<input placeholder="Search driver / location / vehicle" value={q} onChange={e => setQ(e.target.value)} style={{ padding: '4px 8px', width: 260 }} />}>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Table headers={['Driver', 'From', 'To', 'Duration', 'Vehicle #', 'Start', 'End']}>
          {shown.length === 0 && <tr><td style={{ ...td, color: INK.secondary }} colSpan={7}>No moves in this window.</td></tr>}
          {shown.map((p, i) => (
            <tr key={`${p.complete.eventId}-${i}`}>
              <td style={td}>{p.start.userId || '-'}</td><td style={td}>{p.start.location || '-'}</td><td style={td}>{p.complete.location || '-'}</td>
              <td style={td}>{fmtDur(p.complete.timestamp - p.start.timestamp)}</td><td style={td}>{p.start.vehicleNumber || '-'}</td>
              <td style={td}>{fmt(p.start.timestamp)}</td><td style={td}>{fmt(p.complete.timestamp)}</td>
            </tr>
          ))}
        </Table>
      </div>
    </Card>
  );
}

export const CELL_HOVER_CSS = `.jj-heat-cell:hover{transform:scale(1.08);z-index:2}.jj-heat-cell:focus-visible{transform:scale(1.08);z-index:2;outline:2px solid ${CHROME.outline};outline-offset:1px}`;
